import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { MODULE_CONFIG_REGISTRY } from "@/store/slices/screenConfig/moduleConfigRegistry";

export type ScreenLevelSelectionState = Record<
  string,
  {
    brands?: number[];
    markets?: number[];
    segments?: string[];
    secondaryBrands?: number[];
    /**
     * AI models picked on this screen. Only screens with a model picker write
     * it, so other screens' snapshots keep the last value instead of wiping it.
     */
    models?: number[];
  }
>;

const initialState: ScreenLevelSelectionState = {};

/**
 * Slot prefixes of modules declaring `screenLevelSelectionPerModule`
 * (e.g. `"aivisibility"`). `buildContextKey` keys those modules' screens as
 * `` `${prefix}|${screenType}` `` so they never share a slot with the same
 * screen type in another module.
 */
const PER_MODULE_PREFIXES: string[] = Object.entries(MODULE_CONFIG_REGISTRY)
  .filter(([, moduleConfig]) => moduleConfig.config.screenLevelSelectionPerModule === true)
  .map(([moduleName]) => moduleName.replace(/[-_]/g, "").toLowerCase());

/**
 * Split a context key into the module namespace that owns it and the screen
 * part within that namespace.
 *
 * Global keys — `"brand"`, `"market"`, `"PRE_POST"`,
 * `"brand|POINT_IN_TIME|DIFFERENTIATION"` — have a `null` namespace even
 * though some of them contain a `|`, so the separator alone cannot be used to
 * detect module scoping; only a known per-module prefix can.
 */
function splitContextKey(key: string): {
  namespace: string | null;
  screen: string;
} {
  const prefix = PER_MODULE_PREFIXES.find((p) => key.startsWith(`${p}|`));
  return prefix
    ? { namespace: prefix, screen: key.slice(prefix.length + 1) }
    : { namespace: null, screen: key };
}

export const screenLevelSelectionSlice = createSlice({
  name: "screenLevelSelection",
  initialState,
  reducers: {
    updateScreenLevelSelection: (
      state: ScreenLevelSelectionState,
      action: PayloadAction<{
        screenType: keyof ScreenLevelSelectionState;
        value: Partial<
          ScreenLevelSelectionState[keyof ScreenLevelSelectionState]
        >;
      }>,
    ) => {
      const { screenType, value } = action.payload;
      const models = value.models ?? state[screenType]?.models;
      state[screenType] = models ? { ...value, models } : value;

      // Segments and markets are shared across the screens of one module, so a
      // write to any of its slots is mirrored onto the others. The mirror must
      // stop at the module boundary: a module with
      // `screenLevelSelectionPerModule` keeps its own slots precisely so its
      // screens do not share context with other modules' screens of the same
      // type. Without this guard, writing `"aivisibility|brand"` would push AI
      // Visibility's market onto the global `"brand"` slot (clobbering the
      // market the user picked in Equity), and writing `"brand"` would push
      // Equity's market into `"aivisibility|brand"` — the two directions the
      // per-module slots exist to keep apart.
      const source = splitContextKey(String(screenType));

      const mirrorToNamespace = (
        apply: (slot: ScreenLevelSelectionState[string]) => void,
        dedicatedScreen: string,
      ) => {
        Object.keys(state).forEach((key) => {
          const target = splitContextKey(key);
          if (target.namespace !== source.namespace) return;
          // The dedicated screen for this dimension owns its own value (the
          // By-Market screen's markets, the By-Segment screen's segments) and
          // is never overwritten by another screen's snapshot.
          if (target.screen === dedicatedScreen) return;
          if (state[key]) apply(state[key]);
        });
      };

      if (!source.screen.startsWith("segment") && value?.segments) {
        mirrorToNamespace((slot) => {
          slot.segments = value.segments;
        }, "segment");
      }

      if (
        !source.screen.startsWith("market") &&
        value?.markets &&
        value.markets.length > 0
      ) {
        mirrorToNamespace((slot) => {
          slot.markets = value.markets;
        }, "market");
      }
    },
    resetScreenLevelSelection: () => {
      return initialState;
    },
  },
});

export const { updateScreenLevelSelection, resetScreenLevelSelection } =
  screenLevelSelectionSlice.actions;

export default screenLevelSelectionSlice.reducer;
